/*
  ===== CUSTOM CART GWP / REWARDS LOGIC =====

  Handles:
  - Free shipping + free gift progress bar
  - Auto add/remove gift product
  - Exclude gift from qualifying subtotal
  - Locked gift preview row
  - Locked/unlocked milestone icons
  - Duplicate gift prevention
  - Force free gift quantity to 1
  - Exclude logged-in TRADE customers from GWP
  - Mark real gift line item for styling
  - Move BSS B2B trade discount form into the native cart accordion
*/

(function () {
  if (window.customCartGwpInitialized) return;
  window.customCartGwpInitialized = true;

  class CustomCartGwp {
    constructor() {
      this.settings = window.themeGwp || {};

      this.enabled = Boolean(this.settings.enabled);
      this.variantId = Number(this.settings.variantId);
      this.productHandle = this.settings.productHandle;

      /*
        This value must be passed from Liquid in window.themeGwp.
        Example:
        isTradeCustomer: {% if customer and customer.tags contains 'TRADE' %} true {% else %} false {% endif %},
      */
      this.isTradeCustomer = Boolean(
        this.settings.isTradeCustomer ||
          this.settings.customerIsTrade ||
          this.settings.tradeCustomer
      );

      this.threshold = Number(this.settings.threshold || 0);
      this.shippingThreshold = Number(this.settings.shippingThreshold || 0);
      this.maxThreshold = Math.max(this.threshold, this.shippingThreshold);

      this.isProcessing = false;
      this.updateTimer = null;
      this.observer = null;

      this.init();
    }

    init() {
      /*
        If the GWP feature is disabled, or the logged-in customer is tagged TRADE,
        remove any existing GWP item and do not run the GWP rewards logic.
      */
      if (!this.enabled || this.isTradeCustomer) {
        if (this.isTradeCustomer) {
          document.documentElement.classList.add('custom-gwp-trade-customer');
          this.hideGwpUiForExcludedCustomer();
          setTimeout(() => this.hideGwpUiForExcludedCustomer(), 500);
          setTimeout(() => this.hideGwpUiForExcludedCustomer(), 1500);
        }

        this.removeGiftIfExists();
        return;
      }

      if (!this.variantId || !this.threshold || !this.maxThreshold) {
        console.warn('Custom GWP: Missing required settings.');
        return;
      }

      this.redirectGiftProductPage();
      this.bindEvents();
      this.patchFetch();
      this.observeCartDrawer();

      this.scheduleUpdate(100);
      this.scheduleUpdate(1000);
    }

    bindEvents() {
      const refreshEvents = [
        'theme:cart:refresh',
        'theme:cart:reload',
        'theme:cart:update',
        'theme:cart:change',
        'cart:refresh',
        'cart:updated',
        'cart:change',
        'ajaxProduct:added',
        'product:added'
      ];

      refreshEvents.forEach((eventName) => {
        document.addEventListener(eventName, () => {
          this.scheduleUpdate(500);
        });
      });

      document.addEventListener('click', (event) => {
        if (
          event.target.closest('[data-cart-drawer-open]') ||
          event.target.closest('[data-cart-drawer-toggle]') ||
          event.target.closest('[data-cart-drawer-close]') ||
          event.target.closest('[data-quantity-plus]') ||
          event.target.closest('[data-quantity-minus]') ||
          event.target.closest('[data-item-remove]') ||
          event.target.closest('[data-remove-item]') ||
          event.target.closest('[data-cart-item-remove]') ||
          event.target.closest('quantity-counter') ||
          event.target.closest('quantity-input') ||
          event.target.closest('cart-remove-button') ||
          event.target.closest('[name="add"]')
        ) {
          this.scheduleUpdate(900);
        }
      });

      document.addEventListener('change', (event) => {
        if (
          event.target.matches('input[name="updates[]"]') ||
          event.target.matches('[data-quantity-input]') ||
          event.target.closest('quantity-counter') ||
          event.target.closest('quantity-input')
        ) {
          this.scheduleUpdate(900);
        }
      });
    }

    patchFetch() {
      if (window.customCartGwpFetchPatched) return;
      window.customCartGwpFetchPatched = true;

      const originalFetch = window.fetch;
      const self = this;

      window.fetch = async function () {
        const response = await originalFetch.apply(this, arguments);

        try {
          const input = arguments[0];
          const url =
            typeof input === 'string'
              ? input
              : input && input.url
                ? input.url
                : '';

          if (self.isCartMutationUrl(url)) {
            self.scheduleUpdate(700);
          }
        } catch (error) {
          console.warn('Custom GWP fetch patch error:', error);
        }

        return response;
      };
    }

    isCartMutationUrl(url) {
      return (
        url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear')
      );
    }

    observeCartDrawer() {
      const drawer = document.querySelector('#cart-drawer');

      if (!drawer) return;

      this.observer = new MutationObserver(() => {
        this.markGiftItemInDom();
      });

      this.observer.observe(drawer, {
        childList: true,
        subtree: true
      });
    }

    scheduleUpdate(delay = 300) {
      clearTimeout(this.updateTimer);

      this.updateTimer = setTimeout(() => {
        this.updateEverything();
      }, delay);
    }

    async getCart() {
      const response = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Custom GWP: Failed to fetch cart.');
      }

      return await response.json();
    }

    isGiftItem(item) {
      if (!item) return false;

      const isSelectedGiftVariant = Number(item.variant_id) === this.variantId;
      const isSelectedGiftHandle = this.productHandle && item.handle === this.productHandle;

      /*
        Treat the selected gift product/variant as the GWP item.
        This also catches cases where another app/theme mechanism adds the
        same gift product without our custom line item properties.
      */
      return isSelectedGiftVariant || isSelectedGiftHandle;
    }

    getSubtotalWithoutGift(cart) {
      return cart.items.reduce((total, item) => {
        if (this.isGiftItem(item)) return total;
        return total + item.final_line_price;
      }, 0);
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const currency = window.Shopify?.currency?.active || 'AUD';
      const locale = document.documentElement.lang || 'en-AU';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency
        })
          .format(amount)
          .replace('.00', '');
      } catch (error) {
        return `$${amount.toFixed(2).replace('.00', '')}`;
      }
    }

    replaceAmount(message, cents) {
      if (!message) return '';
      return message.replace('[amount]', this.formatMoney(cents));
    }

    hideGwpUiForExcludedCustomer() {
      const gwpElements = document.querySelectorAll(
        '[data-custom-cart-rewards], [data-gwp-preview]'
      );

      gwpElements.forEach((element) => {
        element.hidden = true;
        element.style.display = 'none';
      });
    }

    async updateEverything() {
      if (this.isProcessing) return;

      if (this.isTradeCustomer) {
        this.hideGwpUiForExcludedCustomer();
        await this.removeGiftIfExists();
        return;
      }

      this.isProcessing = true;

      try {
        const cart = await this.getCart();
        const giftItems = cart.items.filter((item) => this.isGiftItem(item));
        const giftItem = giftItems[0];
        const subtotalWithoutGift = this.getSubtotalWithoutGift(cart);

        this.updateUi(subtotalWithoutGift, giftItems);
        this.markGiftItemInDom();

        /*
          Remove duplicate free gifts.
        */
        if (giftItems.length > 1) {
          await this.removeDuplicateGifts(giftItems);
          this.refreshThemeCart();
          this.scheduleUpdate(900);
          return;
        }

        /*
          Force gift quantity to 1.
        */
        if (giftItem && giftItem.quantity > 1) {
          await this.changeGiftQuantity(giftItem.key, 1);
          this.refreshThemeCart();
          this.scheduleUpdate(900);
          return;
        }

        /*
          Add gift when threshold is reached.
        */
        if (subtotalWithoutGift >= this.threshold && !giftItem) {
          await this.addGift();
          this.refreshThemeCart();
          this.scheduleUpdate(900);
          return;
        }

        /*
          Remove gift when threshold drops below required amount.
        */
        if (subtotalWithoutGift < this.threshold && giftItem) {
          await this.removeGift();
          this.refreshThemeCart();
          this.scheduleUpdate(900);
          return;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isProcessing = false;
      }
    }

    updateUi(subtotalWithoutGift, giftItems) {
      if (this.isTradeCustomer) {
        this.hideGwpUiForExcludedCustomer();
        return;
      }

      const rewards = document.querySelector('[data-custom-cart-rewards]');
      const message = document.querySelector('[data-gwp-message]');
      const progressFill = document.querySelector('[data-gwp-progress-fill]');
      const shippingMarker = document.querySelector('[data-gwp-shipping-marker]');
      const giftMarker = document.querySelector('[data-gwp-gift-marker]');
      const shippingIcon = document.querySelector('[data-gwp-shipping-icon]');
      const giftIcon = document.querySelector('[data-gwp-gift-icon]');
      const preview = document.querySelector('[data-gwp-preview]');

      if (!rewards || !message || !progressFill) return;

      const progress = Math.min((subtotalWithoutGift / this.maxThreshold) * 100, 100);
      const shippingPosition = Math.min((this.shippingThreshold / this.maxThreshold) * 100, 100);

      progressFill.style.width = `${progress}%`;

      if (shippingMarker) {
        shippingMarker.style.left = `${shippingPosition}%`;
      }

      if (giftMarker) {
        giftMarker.style.left = '100%';
      }

      const hasUnlockedShipping = subtotalWithoutGift >= this.shippingThreshold;
      const hasUnlockedGift = subtotalWithoutGift >= this.threshold;
      const hasGiftItem = giftItems.length > 0;

      rewards.classList.toggle('is-shipping-unlocked', hasUnlockedShipping);
      rewards.classList.toggle('is-gift-unlocked', hasUnlockedGift);

      this.setMarkerState(shippingMarker, hasUnlockedShipping);
      this.setMarkerState(giftMarker, hasUnlockedGift);

      if (shippingIcon) {
        this.setIcon(
          shippingIcon,
          hasUnlockedShipping ? this.settings.shippingIconUnlocked : this.settings.shippingIconLocked,
          'Free shipping'
        );
      }

      if (giftIcon) {
        this.setIcon(
          giftIcon,
          hasUnlockedGift ? this.settings.giftIconUnlocked : this.settings.giftIconLocked,
          'Free gift'
        );
      }

      message.classList.remove('custom-cart-rewards__message--success');

      if (subtotalWithoutGift < this.shippingThreshold) {
        const remaining = this.shippingThreshold - subtotalWithoutGift;

        message.innerHTML = this.replaceAmount(
          this.settings.shippingMessage || 'You are [amount] away from free shipping.',
          remaining
        );
      } else if (subtotalWithoutGift < this.threshold) {
        const remaining = this.threshold - subtotalWithoutGift;

        message.innerHTML = this.replaceAmount(
          this.settings.shippingUnlockedMessage ||
            'Free shipping unlocked. Spend [amount] more for a free cosmetic case.',
          remaining
        );
      } else {
        message.classList.add('custom-cart-rewards__message--success');

        message.innerHTML =
          `<span class="custom-cart-rewards__success-icon">✓</span> ` +
          (this.settings.gwpUnlockedMessage ||
            "You've unlocked free shipping and a free cosmetic case!");
      }

      /*
        Show locked preview only when cart has paid items and gift is not unlocked.
        The preview snippet should sit outside <cart-items>, because Broadcast
        can replace cart item contents after quantity changes.
      */
      if (preview) {
        const shouldShowPreview =
          subtotalWithoutGift > 0 &&
          subtotalWithoutGift < this.threshold &&
          !hasGiftItem;

        preview.hidden = !shouldShowPreview;
      }
    }

    setMarkerState(marker, isUnlocked) {
      if (!marker) return;

      marker.classList.toggle('is-unlocked', isUnlocked);
      marker.classList.toggle('is-locked', !isUnlocked);
    }

    setIcon(container, iconUrl, altText) {
      if (!iconUrl) return;

      const currentImage = container.querySelector('img');

      if (currentImage && currentImage.getAttribute('src') === iconUrl) return;

      container.innerHTML = `
        <img
          src="${iconUrl}"
          alt="${altText}"
          loading="lazy"
        >
      `;
    }

    async addGift() {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: this.variantId,
              quantity: 1,
              properties: {
                _free_gift: 'true',
                _free_gift_type: 'cosmetic_case'
              }
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error('Custom GWP: Failed to add gift.');
      }
    }

    async removeGift() {
      const cart = await this.getCart();
      const updates = {};

      cart.items.forEach((item) => {
        if (this.isGiftItem(item)) {
          updates[item.key] = 0;
        }
      });

      if (!Object.keys(updates).length) return;

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Custom GWP: Failed to remove gift.');
      }
    }

    async removeDuplicateGifts(giftItems) {
      const updates = {};

      giftItems.forEach((item, index) => {
        if (index > 0) {
          updates[item.key] = 0;
        }
      });

      if (!Object.keys(updates).length) return;

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Custom GWP: Failed to remove duplicate gifts.');
      }
    }

    async changeGiftQuantity(key, quantity) {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      });

      if (!response.ok) {
        throw new Error('Custom GWP: Failed to correct gift quantity.');
      }
    }

    async removeGiftIfExists() {
      try {
        if (!this.variantId) return;

        const cart = await this.getCart();
        const updates = {};

        cart.items.forEach((item) => {
          if (this.isGiftItem(item)) {
            updates[item.key] = 0;
          }
        });

        if (!Object.keys(updates).length) return;

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ updates })
        });

        this.refreshThemeCart();
      } catch (error) {
        console.error(error);
      }
    }

    markGiftItemInDom() {
      if (!this.productHandle) return;

      document.querySelectorAll('.custom-gwp-gift-item').forEach((item) => {
        item.classList.remove('custom-gwp-gift-item');
        item.classList.remove('is-earned-gift');
      });

      const safeHandle = this.productHandle.replace(/"/g, '\\"');
      const giftLinks = document.querySelectorAll(`a[href*="/products/${safeHandle}"]`);

      giftLinks.forEach((link) => {
        const row =
          link.closest('.cart__item') ||
          link.closest('.cart-item') ||
          link.closest('[data-cart-item]') ||
          link.closest('.line-item') ||
          link.closest('.drawer__item') ||
          link.closest('li');

        if (!row) return;

        row.classList.add('custom-gwp-gift-item', 'is-earned-gift');
        row.setAttribute('data-custom-gwp-gift-item', 'true');
      });
    }

    refreshThemeCart() {
      const events = [
        'theme:cart:refresh',
        'theme:cart:reload',
        'cart:refresh',
        'cart:updated'
      ];

      events.forEach((eventName) => {
        document.dispatchEvent(
          new CustomEvent(eventName, {
            bubbles: true
          })
        );
      });
    }

    redirectGiftProductPage() {
      if (!this.productHandle) return;

      const currentPath = window.location.pathname.replace(/\/$/, '');
      const giftPath = `/products/${this.productHandle}`;

      if (currentPath === giftPath) {
        window.location.replace('/');
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new CustomCartGwp();
    });
  } else {
    new CustomCartGwp();
  }
})();

/*
  ===== CUSTOM BSS TRADE DISCOUNT ACCORDION FIX =====
  Moves the BSS B2B discount form into our native Broadcast accordion.
*/

(function () {
  if (window.customBssTradeDiscountInitialized) return;
  window.customBssTradeDiscountInitialized = true;

  function moveBssDiscountForm() {
    const mount = document.querySelector('[data-bss-discount-mount]');
    const tradeAccordion = document.querySelector('[data-custom-trade-discount]');
    const bssWrappers = Array.from(
      document.querySelectorAll('.bss-b2b-discount-code-wrapper')
    );

    if (!mount || !tradeAccordion || !bssWrappers.length) {
      document.body.classList.remove('custom-trade-discount-active');
      return;
    }

    document.body.classList.add('custom-trade-discount-active');

    /*
      Keep one BSS form only.
      The BSS app can inject the form again after cart updates, which causes duplicates.
    */
    const existingMountedWrapper = mount.querySelector('.bss-b2b-discount-code-wrapper');
    const primaryWrapper = existingMountedWrapper || bssWrappers[0];

    if (!mount.contains(primaryWrapper)) {
      mount.appendChild(primaryWrapper);
    }

    primaryWrapper.classList.add('custom-trade-discount__bss-wrapper');
    primaryWrapper.removeAttribute('hidden');
    primaryWrapper.style.removeProperty('display');

    /*
      Remove duplicate BSS forms to avoid showing the discount box twice.
    */
    bssWrappers.forEach((wrapper) => {
      if (wrapper !== primaryWrapper) {
        wrapper.remove();
      }
    });

    tradeAccordion.setAttribute('open', 'true');

    const body = tradeAccordion.querySelector('[data-collapsible-body]');

    if (body) {
      body.style.height = 'auto';
    }
  }

  function scheduleMoveBssDiscountForm(delay) {
    setTimeout(moveBssDiscountForm, delay);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      moveBssDiscountForm();
      scheduleMoveBssDiscountForm(500);
      scheduleMoveBssDiscountForm(1500);
      scheduleMoveBssDiscountForm(3000);
    });
  } else {
    moveBssDiscountForm();
    scheduleMoveBssDiscountForm(500);
    scheduleMoveBssDiscountForm(1500);
    scheduleMoveBssDiscountForm(3000);
  }

  document.addEventListener('theme:cart:refresh', function () {
    scheduleMoveBssDiscountForm(500);
  });

  document.addEventListener('cart:refresh', function () {
    scheduleMoveBssDiscountForm(500);
  });

  document.addEventListener('cart:updated', function () {
    scheduleMoveBssDiscountForm(500);
  });

  const observer = new MutationObserver(function () {
    moveBssDiscountForm();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();